import { useEffect, useRef } from "react";

/* ---------------------- Animated Glow Layer ---------------------- */
const GlowLayer = ({
  speed = 0.35,
  size = 1600,
  opacity = 0.6,
  gradientColors,
  invert = false,
  verticalOffset = 120,
}) => {
  const ref = useRef(null);

  useEffect(() => {
    let t = 0;
    let frame;

    const animate = () => {
      t += speed;

      // Full-width horizontal motion
      // The orb moves from -halfWindowWidth to +halfWindowWidth
      const halfWindowWidth = window.innerWidth / 2;
      const xAmplitude = halfWindowWidth + size / 2; // ensures glow always covers edges
      const x = Math.sin(t * 0.002) * xAmplitude;

      const direction = invert ? -1 : 1;
      const y = direction * Math.cos(t * 0.002) * 120 + verticalOffset;

      if (ref.current) {
        ref.current.style.transform = `translate(${x}px, ${y}px)`;
      }

      frame = requestAnimationFrame(animate);
    };

    frame = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(frame);
  }, [speed, invert, verticalOffset, size]);

  return (
    <div
      ref={ref}
      className="absolute -inset-[20%] flex items-center justify-center pointer-events-none"
    >
      <div
        className="rounded-full blur-[220px]"
        style={{
          width: size,
          height: size,
          opacity,
          background:
            gradientColors || "radial-gradient(circle, #E1CAA1 0%, transparent 70%)",
          willChange: "transform",
        }}
      />
    </div>
  );
}

/* ---------------------- Vertical Light Bars ---------------------- */
const LightBars = () => {
  const bars = Array.from({ length: 40 });

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {bars.map((_, i) => (
        <div
          key={i}
          className="absolute inset-y-0 w-[50px] mix-blend-overlay backdrop-blur-[60px]
                     bg-gradient-to-r from-transparent via-black/60 to-[#d9d9d9]"
          style={{ left: `${i * 100}px` }}
        />
      ))}
    </div>
  );
}

/* ---------------------- Background ---------------------- */
const Background = () => {
  return (
    <div className="absolute inset-0 w-full h-full bg-black overflow-hidden">
      {/* First orb - #E1CAA1 */}
      <GlowLayer
        size={900}
        speed={3.2}
        opacity={0.85}
        verticalOffset={200}
        gradientColors="radial-gradient(circle, #E1CAA1 0%, transparent 70%)"
        invert={false}
      />
      {/* Second orb - #9B7431 */}
      <GlowLayer
        size={700}
        speed={3.6}
        opacity={0.75}
        verticalOffset={190}
        gradientColors="radial-gradient(circle, #9B7431 0%, transparent 75%)"
        invert={true}
      />
      {/* Third orb - #E1CAA1 */}
      <GlowLayer
        size={1000}
        speed={2.9}
        opacity={0.8}
        verticalOffset={190}
        gradientColors="radial-gradient(circle, #E1CAA1 0%, transparent 70%)"
        invert={false}
      />
      <LightBars />
    </div>
  );
}

export default Background;
